import React, { useEffect, useState } from "react";

/** Vendors */
import { App, Button, Col, Form, Input, Row, Typography } from "antd";

/** Custom Components */
import SubmitBoundary from "./SubmitBoundary";

/** Custom APIs */
import member from "@api/member";

interface FormFields {
  cellphone: string;
  email: string;
  message: string;
  name: string;
}

function ContactForm(): React.ReactNode {
  const [disabled, setDisabled] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [response, setResponse] = useState<string>("");
  const { notification } = App.useApp();
  const [form] = Form.useForm();

  useEffect(() => {
    window?.grecaptcha?.ready(() => {
      setDisabled(false);
    });
  }, [window?.grecaptcha]);

  const actions = {
    onFinish: async () => {
      const values = form.getFieldsValue(true);
      setLoading(true);

      const token = await grecaptcha.execute(
        String(import.meta.env.VITE_REACT_APP_GOOGLE_RECAPTCHA_SITE_KEY),
        { action: "submit" }
      );

      const response = await member.contact.member({ member: values, token });

      if (response.message === "success") {
        setResponse("success");

        notification.success({
          duration: 5,
          description:
            "We received your contact request and will be in touch shortly",
          message: "Success!",
        });

        form.resetFields();
      } else {
        setResponse("failed");

        notification.info({
          duration: 5,
          description:
            "We encountered an issue with this request. Please email or call us instead. We apologize for the inconvience",
          message: "Darn It.",
        });
      }
      setLoading(false);
    },
  };

  return (
    <SubmitBoundary loading={loading} response={response}>
      <Form
        form={form}
        layout="vertical"
        name="contact"
        onFinish={actions.onFinish}
        size="large"
      >
        <Row gutter={[12, 12]}>
          <Col xs={12}>
            <Form.Item
              name="given_name"
              rules={[{ message: "First names are cool", required: true }]}
            >
              <Input placeholder="John" size="large" />
            </Form.Item>
          </Col>
          <Col xs={12}>
            <Form.Item
              name="family_name"
              rules={[{ message: "Last names are cool", required: true }]}
            >
              <Input placeholder="Smith" size="large" />
            </Form.Item>
          </Col>
        </Row>

        <Form.Item name="email" rules={[{ required: true }]}>
          <Input placeholder="Enter Your Email" type="email" />
        </Form.Item>
        <Form.Item name="phone_number" rules={[{ required: true }]}>
          <Input placeholder="Enter Your Telephone Number" type="tel" />
        </Form.Item>
        <Form.Item name="message" rules={[{ required: true }]}>
          <Input.TextArea
            placeholder="Description of Service / Project"
            minLength={20}
            rows={5}
          />
        </Form.Item>
        <Button
          block
          disabled={disabled}
          htmlType="submit"
          loading={loading}
          size="large"
          type="primary"
        >
          Submit
        </Button>
      </Form>
    </SubmitBoundary>
  );
}

export default ContactForm;
